const PREFS_KEY = "nema_voice_guidance_v1";

const defaultPrefs = {
  enabled: false,
  autoSpeak: false,
};

export function isSpeechSynthesisSupported() {
  return typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
}

export function getVoiceGuidancePrefs() {
  if (typeof window === "undefined") return { ...defaultPrefs };
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...defaultPrefs };
    const parsed = JSON.parse(raw);
    return {
      enabled: Boolean(parsed?.enabled),
      autoSpeak: Boolean(parsed?.autoSpeak),
    };
  } catch {
    return { ...defaultPrefs };
  }
}

export function setVoiceGuidancePrefs(partial) {
  const next = { ...getVoiceGuidancePrefs(), ...partial };
  if (typeof window !== "undefined") {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("nema:voice-guidance-prefs", { detail: next }));
  }
  return next;
}

/**
 * Plain-language script for text-to-speech from workflow guide props.
 */
export function buildGuideSpeechText({
  step,
  totalSteps,
  title,
  instruction,
  nextHint,
  steps,
}) {
  const parts = [];

  if (Number.isFinite(step) && Number.isFinite(totalSteps) && totalSteps > 0) {
    parts.push(`You are on step ${step} of ${totalSteps}.`);
  }

  if (title) {
    parts.push(title.endsWith(".") ? title : `${title}.`);
  }

  if (Array.isArray(steps) && steps.length > 0) {
    const current = steps.find((s) => s.state === "current");
    const done = steps.filter((s) => s.state === "done").map((s) => s.label);
    if (current?.label) {
      parts.push(`Current stage: ${current.label}.`);
    }
    if (done.length > 0) {
      parts.push(`Completed: ${done.join(", ")}.`);
    }
  }

  if (instruction) {
    parts.push(instruction.endsWith(".") ? instruction : `${instruction}.`);
  }

  if (nextHint) {
    const hint = nextHint.endsWith(".") ? nextHint : `${nextHint}.`;
    parts.push(`Next step. ${hint}`);
  }

  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function pickVoice(lang = "en-IN") {
  if (!isSpeechSynthesisSupported()) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  return (
    voices.find((v) => v.lang === lang) ||
    voices.find((v) => v.lang.startsWith("en-IN")) ||
    voices.find((v) => v.lang.startsWith("en")) ||
    voices[0]
  );
}

export function stopSpeaking() {
  if (isSpeechSynthesisSupported()) {
    window.speechSynthesis.cancel();
  }
}

/**
 * Speak text using the Web Speech API. Returns a cancel function.
 */
export function speakText(text, options = {}) {
  stopSpeaking();

  const trimmed = String(text || "").trim();
  if (!trimmed || !isSpeechSynthesisSupported()) {
    options.onEnd?.();
    return () => {};
  }

  let cancelled = false;
  const utterance = new SpeechSynthesisUtterance(trimmed);
  utterance.lang = options.lang || "en-IN";
  utterance.rate = options.rate ?? 0.92;
  utterance.pitch = options.pitch ?? 1;

  const assignVoiceAndSpeak = () => {
    if (cancelled) return;
    const voice = pickVoice(utterance.lang);
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
  };

  utterance.onstart = () => {
    if (!cancelled) options.onStart?.();
  };
  utterance.onend = () => {
    if (cancelled) return;
    cancelled = true;
    options.onEnd?.();
  };
  utterance.onerror = () => {
    if (cancelled) return;
    cancelled = true;
    options.onError?.();
    options.onEnd?.();
  };

  const voices = window.speechSynthesis.getVoices();
  if (voices.length === 0) {
    const onVoices = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
      assignVoiceAndSpeak();
    };
    window.speechSynthesis.addEventListener("voiceschanged", onVoices);
    assignVoiceAndSpeak();
  } else {
    assignVoiceAndSpeak();
  }

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    stopSpeaking();
    options.onEnd?.();
  };

  return cancel;
}
